(() => {
  'use strict';

  const root = document.querySelector('[data-sp-first-crunch]');
  if (!root) return;

  const panel = root.querySelector('[data-fc-panel]');
  const entry = root.querySelector('[data-fc-entry]');
  const launcher = root.querySelector('[data-fc-launcher]');
  const dismissEntry = root.querySelector('[data-fc-dismiss]');
  const closeButtons = root.querySelectorAll('[data-fc-close]');
  const form = root.querySelector('#SpFirstCrunchForm');
  const submit = root.querySelector('[data-fc-submit]');
  const status = root.querySelector('[data-fc-status]');
  const copyButton = root.querySelector('[data-fc-copy]');
  const code = root.querySelector('[data-fc-code]');
  const storageKey = 'sp-first-crunch-v1';
  const cooldownMs = 30 * 24 * 60 * 60 * 1000;
  const isMobile = window.matchMedia('(max-width: 749px)');
  let elapsed = 0;
  let lastTick = performance.now();
  let lastIsMobile = isMobile.matches;
  let openedFromEntry = false;
  let inMemoryUntil = 0;

  const privacyAllowsPreferenceStorage = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    return Boolean(privacy && typeof privacy.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed());
  };

  const readUntil = () => {
    try {
      if (privacyAllowsPreferenceStorage()) {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) return Number(JSON.parse(raw).until) || 0;
      }
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw) return Number(JSON.parse(raw).until) || 0;
    } catch (_) {}
    return inMemoryUntil;
  };

  const writeUntil = (until) => {
    inMemoryUntil = until;
    const payload = JSON.stringify({ until });
    try {
      if (privacyAllowsPreferenceStorage()) {
        window.localStorage.setItem(storageKey, payload);
      } else {
        window.sessionStorage.setItem(storageKey, payload);
      }
    } catch (_) {}
  };

  const isSuppressed = () => {
    if (Date.now() < readUntil()) return true;
    const active = document.activeElement;
    if (active && active.matches('input, textarea, select, [contenteditable="true"]')) return true;
    const blockers = document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], cart-drawer[open], .cart-drawer.is-active, #shopify-pc__banner, .shopify-pc__banner, [data-cookie-consent-open="true"]');
    for (const element of blockers) {
      if (element.hidden || element.inert || element.getAttribute('aria-hidden') === 'true') continue;
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      if (style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0 && rect.width > 0 && rect.height > 0) return true;
    }
    if (document.body.classList.contains('overflow-hidden') && !document.body.classList.contains('sp-first-crunch-scroll-lock')) return true;
    return false;
  };

  const rememberExposure = () => writeUntil(Date.now() + cooldownMs);

  const showLauncher = () => {
    entry.hidden = false;
    launcher.setAttribute('aria-expanded', 'false');
  };

  const closePanel = (remember = true) => {
    root.classList.remove('sp-first-crunch--mobile-open');
    panel.hidden = true;
    panel.setAttribute('aria-modal', 'false');
    document.body.classList.remove('sp-first-crunch-scroll-lock');
    if (remember) rememberExposure();
    showLauncher();
    if (openedFromEntry) launcher.focus({ preventScroll: true });
    openedFromEntry = false;
  };

  const openPanel = (source) => {
    if (panel.hidden === false) return;
    entry.hidden = true;
    launcher.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    if (isMobile.matches) {
      root.classList.add('sp-first-crunch--mobile-open');
      panel.setAttribute('aria-modal', 'true');
      document.body.classList.add('sp-first-crunch-scroll-lock');
      openedFromEntry = source === 'launcher';
      const firstControl = panel.querySelector('[data-fc-email], [data-fc-close]');
      if (firstControl) requestAnimationFrame(() => firstControl.focus({ preventScroll: true }));
    } else {
      panel.setAttribute('aria-modal', 'false');
      openedFromEntry = source === 'launcher';
      if (openedFromEntry) {
        const email = panel.querySelector('[data-fc-email]');
        if (email) requestAnimationFrame(() => email.focus({ preventScroll: true }));
      }
    }
    rememberExposure();
  };

  const tick = (now) => {
    const delta = Math.min(now - lastTick, 1000);
    lastTick = now;
    if (isMobile.matches !== lastIsMobile) {
      lastIsMobile = isMobile.matches;
      elapsed = 0;
    }
    if (document.visibilityState === 'visible' && !isSuppressed() && panel.hidden) {
      elapsed += delta;
      const threshold = 5000;
      if (elapsed >= threshold) {
        openPanel('timer');
      }
    }
    window.requestAnimationFrame(tick);
  };

  isMobile.addEventListener('change', (event) => {
    elapsed = 0;
    lastTick = performance.now();
    lastIsMobile = event.matches;
    if (panel.hidden) return;
    if (event.matches) {
      root.classList.add('sp-first-crunch--mobile-open');
      panel.setAttribute('aria-modal', 'true');
      document.body.classList.add('sp-first-crunch-scroll-lock');
    } else {
      root.classList.remove('sp-first-crunch--mobile-open');
      panel.setAttribute('aria-modal', 'false');
      document.body.classList.remove('sp-first-crunch-scroll-lock');
    }
  });

  launcher.addEventListener('click', () => openPanel('launcher'));
  closeButtons.forEach((button) => button.addEventListener('click', () => closePanel()));
  root.addEventListener('click', (event) => {
    if (root.classList.contains('sp-first-crunch--mobile-open') && event.target === root) closePanel();
  });
  if (dismissEntry) dismissEntry.addEventListener('click', () => {
    entry.hidden = true;
    rememberExposure();
  });
  document.addEventListener('keydown', (event) => {
    if (panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      return;
    }
    if (event.key !== 'Tab' || !root.classList.contains('sp-first-crunch--mobile-open')) return;
    const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (form && submit && status) {
    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      submit.disabled = true;
      submit.textContent = 'Signing up…';
      status.textContent = '';
    });
  }

  if (copyButton && code) {
    copyButton.addEventListener('click', async () => {
      const value = code.textContent.trim();
      try {
        await navigator.clipboard.writeText(value);
        copyButton.textContent = 'Copied';
        copyButton.setAttribute('aria-label', 'Copied');
      } catch (_) {
        code.focus?.();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(code);
        selection.removeAllRanges();
        selection.addRange(range);
        copyButton.textContent = 'Select and copy';
      }
    });
  }

  const submitted = Boolean(root.querySelector('[data-fc-success-marker]'));
  if (submitted) {
    rememberExposure();
    if (!panel.hidden) panel.scrollTop = 0;
  }
  window.requestAnimationFrame(tick);
})();
